import { defineStrictThemeOverrides } from "@dropbox/dig-foundations";
import { hexToHsva } from "@uiw/color-convert";
import { isValidHexColor } from "../utils";
import {
  azaleaTheme,
  canopyTheme,
  cloudTheme,
  crimsonTheme,
  goldTheme,
  limeTheme,
  navyTheme,
  oceanTheme,
  orchidTheme,
  pinkTheme,
  plumTheme,
  rustTheme,
  sunsetTheme,
  tangerineTheme,
  vividVargasTheme,
  zenTheme,
} from "./themeDefinitions";

import type { ThemeFamilyKey } from "@dropbox/dash-component-library";
import type { AvailableThemeOverridesStrict } from "@dropbox/dig-foundations";

type ThemeDefinition = {
  primary: AvailableThemeOverridesStrict;
  subtle: AvailableThemeOverridesStrict;
};

export enum StackTheme {
  Ocean,
  Plum,
  Azalea,
  Crimson,
  Rust,
  Gold,
  Canopy,
  Navy,
  Cloud,
  Zen,
  Sunset,
  Pink,
  Tangerine,
  Lime,
  Orchid,
  VividVargas,
  Custom,
}

/**
 * The list of names of stack theme colors. Define these since iterating over
 * enum values will loop over 2x the number of entries
 */

export const stackThemeColorNames = Object.values(StackTheme).filter(
  (val) => typeof val === "string",
) as string[];
/**
 * Given a color index, returns the color name, or "unknown" if the color index
 * is invalid
 */

export const getThemeColorName = (colorIndex: number): ThemeFamilyKey => {
  return stackThemeColorNames[colorIndex] as ThemeFamilyKey;
};

export const getDefaultColorIndex = () => {
  return StackTheme.Ocean;
};

export const getTheme = (
  theme?: StackTheme,
  customColor?: string,
): ThemeDefinition => {
  switch (theme) {
    case StackTheme.Ocean:
      return oceanTheme;
    case StackTheme.Plum:
      return plumTheme;
    case StackTheme.Azalea:
      return azaleaTheme;
    case StackTheme.Crimson:
      return crimsonTheme;
    case StackTheme.Rust:
      return rustTheme;
    case StackTheme.Gold:
      return goldTheme;
    case StackTheme.Canopy:
      return canopyTheme;
    case StackTheme.Cloud:
      return cloudTheme;
    case StackTheme.Navy:
      return navyTheme;
    case StackTheme.Zen:
      return zenTheme;
    case StackTheme.Sunset:
      return sunsetTheme;
    case StackTheme.Pink:
      return pinkTheme;
    case StackTheme.Tangerine:
      return tangerineTheme;
    case StackTheme.Lime:
      return limeTheme;
    case StackTheme.Orchid:
      return orchidTheme;
    case StackTheme.VividVargas:
      return vividVargasTheme;
    case StackTheme.Custom:
      return getCustomThemeByColor(customColor);
    default:
      return oceanTheme;
  }
};

// Based on the sRGB gamma correction formula, which is a standard used to adjust the linear RGB values to their non-linear representation in the sRGB color space.
// StackOverflow: https://stackoverflow.com/questions/61138110/what-is-the-correct-gamma-correction-function
// https://en.wikipedia.org/wiki/SRGB#Theory_of_the_transformation

const gammaCorrect = (c: number) =>
  c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;

export const getColorLuminance = (hexColor: string): number => {
  const r = parseInt(hexColor.substring(1, 3), 16) / 255;
  const g = parseInt(hexColor.substring(3, 5), 16) / 255;
  const b = parseInt(hexColor.substring(5, 7), 16) / 255;
  // Apply gamma correction
  const R = gammaCorrect(r);
  const G = gammaCorrect(g);
  const B = gammaCorrect(b);

  // Calculate luminance using W3C formula https://www.w3.org/WAI/GL/wiki/Relative_luminance
  const luminance = 0.2126 * R + 0.7152 * G + 0.0722 * B;
  return luminance;
};

export const getCustomThemeByColor = (hexColor?: string): ThemeDefinition => {
  if (!hexColor || !isValidHexColor(hexColor)) {
    return oceanTheme;
  }
  const hex = hexColor.replace("#", "");
  const hsva = hexToHsva(hex);
  const luminance = getColorLuminance(hexColor);

  // Use black text if luminance is high, otherwise use white text
  // DIG token var(--dig-color__text__base) for bright is #1a1918 and for dark is #ffffff
  const textHex = luminance > 0.5 ? "1a1918" : "ffffff";

  return {
    primary: defineStrictThemeOverrides({
      colors: {
        "Primary Surface": {
          bright: `#${hex}`,
          dark: `#${hex}`,
        },
        "Text Base": {
          bright: `#${textHex}`,
          dark: `#${textHex}`,
        },
        // copied from subtleTheme inside themeDefinitions
        "Text Subtle": {
          bright:
            luminance > 0.5
              ? "rgba(0, 0, 0, 0.58)"
              : "rgba(255, 255, 255, 0.67)",
          dark:
            luminance > 0.5
              ? "rgba(0, 0, 0, 0.58)"
              : "rgba(255, 255, 255, 0.67)",
        },
      },
    }),
    subtle: defineStrictThemeOverrides({
      colors: {
        "Primary Surface": {
          bright: `hsl(${hsva.h}, ${hsva.s}%, 90%)`,
          dark: `hsl(${hsva.h}, ${hsva.s}%, 90%)`,
        },
      },
    }),
  };
};
